import React, { useEffect, useState, useRef } from 'react'
import { Typography } from '@grammarly/badger-ui'

import styles from './progress-bar.module.scss'

const MAX_POST_SUBMIT_STREAM_PROGRESS_WAIT_TIME = 10000
const MAX_POST_SUBMIT_STREAM_PROGRESS_WAIT_PERCENTAGE = 96
const POST_SUBMIT_STREAM_PROGRESS_INTERVAL = 150

/**
 * Calculate perceived progress to indicate which step the user is at, before form submission.
 */
const calculateProgress = (step: number, steps: number) => {
  let fracComplete = step / (steps + 1)
  if (fracComplete > 0) {
    // Slightly weight progress toward earlier steps to improve the feeling of progress.
    fracComplete += 0.1 * (1 - fracComplete)
  }
  return Math.floor(fracComplete * 100)
}

interface ProgressBarProps {
  step: number
  steps: number
  // After the output has started streaming in but before it is completely shown
  isStreamingResults: boolean
  isComplete: boolean
  progressBarLabel: string
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  step,
  steps,
  isComplete,
  isStreamingResults,
  progressBarLabel,
}) => {
  const [progress, setProgress] = useState(calculateProgress(step, steps))
  const [streamProgressTimePassed, setStreamProgressTimePassed] = useState(0)
  const postStreamIntervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined)

  useEffect(() => {
    setProgress(calculateProgress(step, steps))
  }, [step, steps])

  useEffect(() => {
    if (isComplete) {
      setProgress(100)
    }
  }, [isComplete])

  // On form submission, but before the entire stream response is completed,
  // show the user streaming progress till an arbitrary time and till a max progress percentage.
  useEffect(() => {
    if (isStreamingResults) {
      const intervalId = setInterval(() => {
        setProgress((p) => (p < MAX_POST_SUBMIT_STREAM_PROGRESS_WAIT_PERCENTAGE ? p + 2.5 : p))
        setStreamProgressTimePassed((sptp) => sptp + POST_SUBMIT_STREAM_PROGRESS_INTERVAL)
      }, POST_SUBMIT_STREAM_PROGRESS_INTERVAL)
      postStreamIntervalRef.current = intervalId

      return () => {
        clearInterval(intervalId)
      }
    }
  }, [isStreamingResults])

  useEffect(() => {
    if (
      streamProgressTimePassed >= MAX_POST_SUBMIT_STREAM_PROGRESS_WAIT_TIME ||
      progress >= MAX_POST_SUBMIT_STREAM_PROGRESS_WAIT_PERCENTAGE ||
      isComplete
    ) {
      if (postStreamIntervalRef.current) {
        clearInterval(postStreamIntervalRef.current)
      }
    }
  }, [streamProgressTimePassed, progress, isComplete])

  const progressText = isComplete ? 'Done!' : isStreamingResults ? 'Generating results...' : `Step ${step + 1}`

  return (
    <div className={styles.progressContainer}>
      <Typography.C4 as="label" id={progressBarLabel}>
        {progressText}
      </Typography.C4>
      <div
        className={styles.progressBar}
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={steps}
        aria-valuenow={step}
        aria-labelledby={progressBarLabel}
      >
        <div className={styles.progress} style={{ width: `${progress}%` }}></div>
      </div>
    </div>
  )
}
